import { isFulfilled } from "@reduxjs/toolkit";
import { useMemo, useState } from "react";
import { FieldValues, FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../../../../app/hooks";
import FormSection from "../../../../../common/components/functional/formSection/FormSection";
import isEmpty from "../../../../../common/helpers/isEmpty";
import useIsLoading from "../../../../../hooks/useIsLoading";
import { changeAssignmentSalaryOverride } from "../../../../talents/talentsSlice";
import SalaryComponents from "../../../editForms/salaries/SalaryComponents";

type Props = {
  canEdit: boolean;
};

const AssignmentSalary = ({ canEdit }: Props) => {
  const [edit, setEdit] = useState<boolean>(false);
  const [resetToEmpty, setResetToEmpty] = useState<boolean>(false);

  const assignment = useAppSelector((state) => state.talents.assignment);
  const dispatch = useAppDispatch();
  const isTesSalaryComponentsLoading = useIsLoading(["tesSalaryComponents"]);

  const { t } = useTranslation();
  const tesId = assignment?.details?.tesId;

  // TODO: change this in YLPE when employmentSalary is shown in assignment
  const defaultValues = useMemo(() => {
    // empty defaults if salaryOverride is empty
    if (isEmpty(assignment.details.salaryOverride) && !edit) {
      return {};
    }

    // when editing, display employmentSalary values as default
    // so that total salaries can be calculated
    // and the original values to be overridden are visible.
    if (isEmpty(assignment.details.salaryOverride) && edit) {
      return { ...assignment.employmentSalary };
    }

    // when salaryOverride is set, display the overridden values and
    // fill the rest of the values with the employtmentSalary values
    if (!isEmpty(assignment.details.salaryOverride)) {
      return {
        ...assignment.employmentSalary,
        ...assignment.details.salaryOverride,
      };
    }
  }, [assignment.details.salaryOverride, assignment.employmentSalary, edit]);

  const methods = useForm({
    defaultValues: defaultValues,
    mode: "onChange",
  });

  const onSubmit = (data: FieldValues) => {
    setEdit(false);

    // dispatch only dirty fields
    const overridableComponents: { [key: string]: string } = {};
    for (const [key, value] of Object.entries(data.components)) {
      let isDirtyField = false;

      if (
        typeof methods.formState.dirtyFields.components === "object" &&
        methods.formState.dirtyFields.components !== null
      ) {
        isDirtyField = Object.keys(
          methods.formState.dirtyFields.components,
        ).includes(key);
      }

      if (isDirtyField) {
        overridableComponents[key] = value as string;
      }
    }

    const includesRemovables = Object.values(overridableComponents).some(
      (value) => value === "",
    );

    // TODO: not sure if this works as intended
    // when the user removes a single value, the whole override is removed
    const payload = {
      salaryOverride: includesRemovables ? null : overridableComponents,
      assignmentId: assignment.assignmentId,
      version: assignment.details.version,
    };

    dispatch(changeAssignmentSalaryOverride(payload))
      .then((action) => {
        if (isFulfilled(action)) {
          setEdit(false);

          if (includesRemovables) {
            setResetToEmpty(true);
          }
        }
      })
      .finally(() => setResetToEmpty(false));
  };

  const onEdit = () => {
    if (!edit) {
      setEdit(true);
    }
    if (edit) return methods.handleSubmit(onSubmit)();
  };

  const onCancel = () => {
    setEdit(false);
  };

  const editDisabled =
    !assignment?.employmentSalary || isTesSalaryComponentsLoading;

  return (
    <>
      <FormSection
        title={t("talentProfile:assignmentSpecificSalary")}
        onEdit={onEdit}
        edit={edit}
        onCancel={onCancel}
        saveDisabled={!methods.formState.isDirty}
        editDisabled={editDisabled}
        readOnly={!canEdit}
      >
        <FormProvider {...methods}>
          <SalaryComponents
            tesId={tesId}
            defaultValues={defaultValues}
            override
            edit={edit}
            resetToEmpty={resetToEmpty}
            xsCols={[2, 10]}
            salaryType={assignment?.employmentSalaryType}
          />
        </FormProvider>
      </FormSection>
    </>
  );
};

export default AssignmentSalary;
